import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../api/supabase'

export interface ETSFDocument {
  id: string
  doc_type: string
  title: string
  content: string
  version: string
  last_updated_by: string | null
  created_at: string
  updated_at: string
}

export function useETSFDocuments(docType?: string) {
  const [documents, setDocuments] = useState<ETSFDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadDocuments = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      let query = supabase.from('etsf_documents').select('*').order('updated_at', { ascending: false })

      if (docType) {
        query = query.eq('doc_type', docType)
      }

      const { data, error: qErr } = await query
      if (qErr) throw qErr
      setDocuments((data || []) as ETSFDocument[])
    } catch (e: any) {
      setError(e.message || 'Failed to load documents')
    } finally {
      setLoading(false)
    }
  }, [docType])

  useEffect(() => {
    loadDocuments()
  }, [loadDocuments])

  const fetchDocument = useCallback(async (id: string) => {
    const { data, error } = await supabase
      .from('etsf_documents')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return data as ETSFDocument
  }, [])

  const updateDocument = useCallback(
    async (id: string, content: string, lastUpdatedBy: string) => {
      const { data, error } = await supabase
        .from('etsf_documents')
        .update({
          content,
          last_updated_by: lastUpdatedBy,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      await loadDocuments()
      return data as ETSFDocument
    },
    [loadDocuments]
  )

  return {
    documents,
    loading,
    error,
    reload: loadDocuments,
    fetchDocument,
    updateDocument,
  }
}
